
import { Component, computed, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { computedAsync } from 'src/app/shared/signal-utilities';
import { TrackingService } from '../services/tracking.service';
import { Document } from '../models';
import { BackupFileDownloaderService } from 'src/app/shared/download';
import { DocumentInfo, DropDownDataSource, FileGroup, FileItem } from './tracking-document-compare.model';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { VisualCompareComponent } from '../../shared/visual-compare/visual-compare.component';
import { NgTemplateOutlet } from '@angular/common';
import { VisualCompareFileInfoComponent } from '../../shared/visual-compare-file-info/visual-compare-file-info.component';
import { RouterLinkComponent } from '../../shared/router-link/router-link.component';
import { TrackingStatusLabelComponent } from '../tracking-status-label.component';
import { SingleselectboxComponent } from '../../controls/components/basic/singleselectbox/singleselectbox.component';
import { FormsModule } from '@angular/forms';
import { CheckboxComponent } from '../../controls/components/basic/checkbox/checkbox.component';

@Component({
    selector: 'app-tracking-document-compare',
    templateUrl: './tracking-document-compare.component.html',
    styleUrls: ['./tracking-document-compare.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [VisualCompareComponent, NgTemplateOutlet, VisualCompareFileInfoComponent, RouterLinkComponent, TrackingStatusLabelComponent, SingleselectboxComponent, FormsModule, CheckboxComponent]
})
export class TrackingDocumentCompareComponent {
  private trackingService = inject(TrackingService);
  private backupFileDownloader = inject(BackupFileDownloaderService);
  private loadingPanelService = inject(LoadingPanelService);

  leftDocumentId = input<number | null>(null);
  leftInterchangeInId = input<number | null>(null);

  leftDocument = computedAsync(() => this.loadDocumentOrInterchange(this.leftDocumentId(), this.leftInterchangeInId()));
  leftDropDownDataSource = computedAsync(() => this.createDropDownDataSource(this.leftDocument(), this.includeAdditionalFiles()));
  leftDocumentInfo = computed(() =>
    new DocumentInfo(this.backupFileDownloader, this.leftDocumentId(), this.leftInterchangeInId(), this.leftDocument()?.LatestDocumentStatus?.StatusTypeName, this.leftDropDownDataSource(), true));

  rightDocumentId = input<number | null>(null);
  rightInterchangeInId = input<number | null>(null);
  rightDocument = computedAsync(() => this.loadDocumentOrInterchange(this.rightDocumentId(), this.rightInterchangeInId()));
  rightDropDownDataSource = computedAsync(() => this.createDropDownDataSource(this.rightDocument(), this.includeAdditionalFiles()));
  rightDocumentInfo = computed(() =>
    new DocumentInfo(this.backupFileDownloader, this.rightDocumentId(), this.rightInterchangeInId(), this.rightDocument()?.LatestDocumentStatus?.StatusTypeName, this.rightDropDownDataSource()));

  includeAdditionalFiles = signal(false);
  loading = signal(0);
  language = computed(() => this.detectlanguage(this.leftDocumentInfo()?.selectedFile()));

  constructor() {
    // This effect implements the logic for selecting the same file in the right dropdown as in the left dropdown
    effect(() => {
      const lsf = this.leftDocumentInfo()?.selectedFile();
      if (lsf) {
        const tmp = this.rightDocumentInfo().dropDownDataSource.flatMap(e => e.items);
        const rsfs = lsf.fileType === 'AdditionalFiles'
          ? tmp.filter(e => (e.groupKey === lsf.groupKey) && (e.name === lsf.name) && (e.filename === lsf.filename))
          : tmp.filter(e => e.fileType === lsf.fileType);

        // Only navigate if there is exactly one match
        const rsf = rsfs.length === 1 ? rsfs[0] : null;
        this.rightDocumentInfo().selectFile(rsf);
      }
    });

    // This effect shows the loading panel when loading is in progress
    effect(() => {
      if (this.loading() > 0) {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
      } else {
        this.loadingPanelService.hideLoadingPanel();
      }
    });
  }

  beginLoading(): void {
    this.loading.update(n => n + 1);
  }

  endloading(): void {
    this.loading.update(n => n - 1);
  }

  detectlanguage(fileItem: FileItem): string {
    switch (fileItem?.contentType?.toLowerCase()) {
      case 'application/json':
      case 'text/json':
        return 'json';

      case 'application/xml':
      case 'text/xml':
        return 'xml';

      default:
        const ext = fileItem?.filename?.substring(fileItem.filename.lastIndexOf('.') + 1).toLowerCase();
        if (ext === 'json') {
          return 'json';
        }
        if (ext === 'xml') {
          return 'xml';
        }
        return 'plaintext';
    }
  }

  async loadDocumentOrInterchange(documentId: number | null, interchangeInId: number | null): Promise<Document | null> {
    try {
      this.beginLoading();
      const result = documentId > 0
        ? await this.trackingService.getDocuments([documentId], [])
        : interchangeInId > 0
          ? await this.trackingService.getDocuments([], [interchangeInId])
          : null;
      return result?.Documents?.find(() => true) ?? null; // FirstOrDefault()
    }
    catch (err) {
      return null;
    }
    finally {
      this.endloading();
    }
  }

  async createDropDownDataSource(doc: Document | null, includeAdditionalFiles: boolean): Promise<DropDownDataSource> {
    try {
      this.beginLoading();

      const dataSource = [
        new FileGroup('Payload files', [
          new FileItem('Interchange In', 'InterchangeIn', doc?.InterchangeIn?.Filename),
          new FileItem('Message In', 'MessageIn', doc?.FileGuidIn),
          new FileItem('CDM', 'MessageCdm', doc?.FileGuidCdm),
          new FileItem('Interchange Out', 'InterchangeOut', doc?.InterchangeOut?.Filename),
        ]),
        new FileGroup('Files for ingoing interchange',
          includeAdditionalFiles
            ? doc?.InterchangeIn?.AdditionalBackupFiles?.map(e => new FileItem(e.Type, 'AdditionalFiles', e.FileGuid)) ?? []
            : [],
        ),
        new FileGroup('Files for document',
          includeAdditionalFiles
            ? doc?.AdditionalBackupFiles?.map(e => new FileItem(e.Type, 'AdditionalFiles', e.FileGuid)) ?? []
            : [],
        ),
        new FileGroup('Files for outgoing interchange',
          includeAdditionalFiles
            ? doc?.InterchangeOut?.AdditionalBackupFiles?.map(e => new FileItem(e.Type, 'AdditionalFiles', e.FileGuid)) ?? []
            : [],
        ),
      ];

      // Remove items with empty fileIdentifier and then remove groups with no items
      const filteredDataSource = dataSource.filter(group => {
        group.items = group.items.filter(item => item.fileIdentifier && item.fileIdentifier !== '00000000-0000-0000-0000-000000000000');
        return group.items.length > 0;
      });

      // Assign group key to all items
      filteredDataSource.forEach(group => group.items.forEach(item => item.groupKey = group.key));

      // Load all file info in parallel
      await Promise.all(filteredDataSource.flatMap(e => e.items).map(item => item.load(this.backupFileDownloader, false)));

      return filteredDataSource;
    }
    catch (err) {
      return [];
    }
    finally {
      this.endloading();
    }
  }

  selectFileFromUI(documentInfo: DocumentInfo, file: FileItem): void {
    this.beginLoading();
    documentInfo?.selectFile(file)
      .finally(() => this.endloading());
  }

  navigationParams(documentInfo: DocumentInfo): { documentIds?: number[]; interchangeInIds?: number[] } {
    return documentInfo.documentId
      ? { documentIds: [documentInfo.documentId] }
      : { interchangeInIds: [documentInfo.interchangeInId] };
  }
}
